import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TimesheetForm, {
  P45Context,
  TimesheetFormRefType,
  TimesheetFormValues,
} from '../components/TimesheetForm';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { AppDispatch, AppState } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import { useCreateTimesheet } from '@nxsys/hooks/api/timesheets';
import { CreateTimesheetParams } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { PaymentFrequencyEnum } from '@nxsys/constants/AppEnums';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { IRELAND } from '@nxsys/store/constants/keys';
import { WarningFilled } from '@ant-design/icons';
import { Modal, Tag } from 'antd';

const AddNewTimesheet = () => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { goBack } = useGoBackByQuery();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const handleGoBack = () => {
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: '/timesheets',
      excludeParams: ['defaultValues'],
    });
  };

  const timesheetFormRef = useRef<TimesheetFormRefType>(null);

  const { run: createTimesheet } = useCreateTimesheet();
  const [isCreateTsPending, setIsCreateTsPending] = useState(false);
  const [palConfirmed, setPalConfirmed] = useState(false);

  const location = useLocation();
  const locationState = location.state as P45Context | null;

  // Build P45 context from location state (navigated from Employee Details P45 flow
  // for Issue P45, "Make Payment After Leaving" which sets isPAL, or switching to
  // 52-week average holiday pay with a balance left).
  const p45Context: P45Context | null =
    locationState?.isIssueP45 ||
    locationState?.isPAL ||
    locationState?.isChangeHolidayPayMethod
      ? locationState
      : null;

  const [searchParams] = useSearchParams();
  const defaultValuesParams = searchParams.get('defaultValues');
  // Memoize so the parsed object keeps a stable reference across renders.
  // Otherwise JSON.parse returns a new object every render, which makes
  // TimesheetForm's `[defaultValues]` effect re-run and reset the rates /
  // PAL confirmation on every render.
  const defaultValues = useMemo(
    () => (defaultValuesParams ? JSON.parse(defaultValuesParams) : null),
    [defaultValuesParams],
  );

  // Build default values from P45 context (pre-select employee)
  const p45DefaultValues = useMemo(() => {
    if (!p45Context?.selectSub) return null;
    const sub = p45Context.selectSub;
    return {
      subcontractorRef: {
        label: `${sub.forename || ''} ${sub.surname || ''}`.trim(),
        value: sub.id,
        fullData: sub,
      },
    } as Partial<TimesheetFormValues>;
  }, [p45Context]);

  // Determine if the selected employee is a leaver (PAL scenario)
  const isPALTimesheet = useMemo(() => {
    if (isIreland) return false;
    const sub = p45Context?.selectSub;
    if (!sub) return false;
    return !!(sub.dateLeft || sub?.isIssuedP45);
  }, [p45Context, isIreland]);

  // Show PAL warning when navigating from P45 flow with a leaver employee
  useEffect(() => {
    if (isPALTimesheet && !palConfirmed) {
      showPALWarning();
    }
  }, [isPALTimesheet]);

  const showPALWarning = (subData?: any) => {
    const sub = subData || p45Context?.selectSub;
    const employeeName =
      `${sub?.forename || ''} ${sub?.surname || ''}`.trim();
    Modal.confirm({
      icon: null,
      width: 460,
      centered: true,
      content: (
        <div className="text-center">
          <WarningFilled style={{ fontSize: 46, color: '#FAAD14' }} />
          <div className="mt-3 mb-4 text-[18px] font-semibold text-[#1A1D1F]">
            Payment After Leaving (PAL) Warning
          </div>
          <div
            className="mb-4 rounded-lg px-4 py-3 text-left"
            style={{ background: '#FFFBE6', border: '1px solid #FFE58F' }}
          >
            You are creating a timesheet for{' '}
            {employeeName ? <strong>{employeeName}</strong> : 'an employee'} who
            has already left employment.
          </div>
          <div className="text-left">
            <p className="mb-2 font-medium">Please note the following:</p>
            <div style={{ paddingLeft: 8 }}>
              <p className="mb-2">
                This payment will be submitted to HMRC as a{' '}
                <strong>Payment After Leaving</strong>.
              </p>
              <p className="mb-2">
                Tax will be calculated on tax code <strong>0T W1M1</strong> (or
                S0T/C0T for Scottish/Welsh taxpayers).
              </p>
              <p className="mb-0">
                <strong>DO NOT issue a new P45</strong> — the original P45
                remains valid.
              </p>
            </div>
          </div>
        </div>
      ),
      okText: 'I Understand, Continue',
      cancelText: 'Cancel',
      onOk: () => setPalConfirmed(true),
      onCancel: () => {
        if (p45Context) handleGoBack();
      },
    });
  };

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={messages['timesheet.addNew']}
            title=""
          />
          {palConfirmed && <Tag color="warning">PAL</Tag>}
        </div>
      </div>
    );
  }, [messages, palConfirmed]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.cancel']}
          onClick={handleGoBack}
          className="max-sm:hidden"
        />
        <AppButton
          type="primary"
          name={messages['common.save']}
          onClick={() => timesheetFormRef.current?.submit()}
          loading={isCreateTsPending}
          disabled={isCreateTsPending}
        />
      </div>
    );
  }, [messages, isCreateTsPending]);

  const handleSubmit = async (values: TimesheetFormValues) => {
    const subcontractorData = values?.subcontractorRef?.fullData;
    const isLeaver = !!(
      subcontractorData?.dateLeft || subcontractorData?.isIssuedP45
    );

    // Block submit if PAL timesheet and not yet confirmed
    if (isLeaver && !isIreland && !palConfirmed) {
      showPALWarning(subcontractorData);
      return;
    }

    const agencyData = values?.agencyRef?.fullData;
    const paymentFrequency = subcontractorData?.payFrequency;

    const params: Partial<CreateTimesheetParams> = {
      averageRate:
        subcontractorData?.paymentModel === PAYMENT_MODEL_ENUM.PAYEE
          ? subcontractorData?.average52WeekData?.averageHourlyRate
          : 0,
      companyRef: subcontractorData?.companyPayment?.id ?? null,
      endDate: values.endDate ? moment(values.endDate).toISOString() : null,
      invoicingAddress: {
        address: agencyData?.address ?? [],
        city: agencyData?.city ?? '',
        county: agencyData?.county ?? '',
        postCode: agencyData?.postCode ?? '',
        country: agencyData?.country ?? '',
      },
      rates:
        values.rates?.map((rate) => ({
          ...rate,
          rateCode: rate.rateCode?.value ?? '',
          vatDetails: rate.vatDetails?.value ?? '',
        })) ?? [],
      siteId: values.siteId?.value ?? '',
      startDate: values.startDate
        ? moment(values.startDate).toISOString()
        : null,
      subcontractorRef: subcontractorData?.id,
      ...(paymentFrequency === PaymentFrequencyEnum.MONTHLY
        ? {
            monthConfig: Number(values.taxPeriod?.value) ?? 1,
          }
        : {
            taxPeriodConfig: Number(values.taxPeriod?.value) ?? 1,
          }),
      workLocation: {},
      file: null,
    };

    if (!isBureau) {
      params.agencyRef = agencyData?.id ?? null;
      params.assignmentRef = values.assignmentRef?.value ?? null;
    } else {
      params.isBureau = true;
    }

    // P45 leaver timesheet flags
    if (p45Context?.isIssueP45) {
      params.isIssueP45 = true;
    }
    // Mark as PAL if the employee already has a P45 issued
    if (isLeaver) {
      params.isPAL = true;
    }

    setIsCreateTsPending(true);
    if ((values.file as File)?.name) {
      const res = await dispatch(
        commonActions.uploadFileToS3({ file: values.file as File }),
      ).unwrap();
      params.file = res.url;
    }
    await createTimesheet(params as CreateTimesheetParams)
      .then(() => {
        showMessage({
          type: 'success',
          content: messages['timesheet.createSuccessfully'],
        });
        handleGoBack();
      })
      .finally(() => {
        setIsCreateTsPending(false);
      });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <TimesheetForm
        formRef={timesheetFormRef}
        onSubmit={handleSubmit}
        defaultValues={p45DefaultValues ?? defaultValues}
        p45Context={p45Context}
        onSubcontractorChange={() => setPalConfirmed(false)}
      />
    </AppsContainer>
  );
};

export default AddNewTimesheet;
